import { useEffect, useMemo, useState } from "react";
import { Download, ExternalLink, FileText } from "lucide-react";
import { smoothScrollTo } from "../../lib/scroll";
import { CrossPolicyMap } from "./CrossPolicyMap";

const clean = (lines) => lines.map((line) => line.trim()).filter(Boolean);
const slugify = (number, title) => `section-${number}-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;

const parseDocument = (raw) => {
  const blocks = raw.replace(/\r/g, "").trim().split(/\n\s*\n+/).map((block) => clean(block.split("\n"))).filter((block) => block.length);
  const start = blocks.findIndex((lines) => /^1\.\s+/.test(lines[0]));
  const sections = [];
  if (start < 0) return sections;

  blocks.slice(start).forEach((lines) => {
    const heading = lines[0].match(/^(\d+)\.\s+(.+)$/);
    if (heading && Number(heading[1]) === sections.length + 1) {
      const title = [heading[2], ...lines.slice(1)].join(" ").replace(/\s+/g, " ");
      sections.push({ number: heading[1], title, id: slugify(heading[1], title), body: [] });
      return;
    }

    const current = sections[sections.length - 1];
    if (!current) return;
    const bullets = lines.filter((line) => /^[●•]\s*/.test(line));
    if (bullets.length === lines.length) {
      current.body.push({ type: "list", content: bullets.map((line) => line.replace(/^[●•]\s*/, "")) });
      return;
    }

    const content = lines.join(" ").replace(/\s+/g, " ");
    const isSubheading = lines.length === 1 && content.length < 72 && !/[.!?;:]$/.test(content);
    current.body.push({ type: isSubheading ? "subheading" : "paragraph", content });
  });

  return sections;
};

const Toc = ({ sections, mobile = false }) => (
  <ul className={mobile ? "policy-mobile-toc-list" : "policy-toc-list"}>
    {sections.map((section) => (
      <li key={section.id}>
        <a
          href={`#${section.id}`}
          data-testid={`${mobile ? "mobile-" : ""}toc-link-${section.id}`}
          onClick={(event) => {
            event.preventDefault();
            smoothScrollTo(document.getElementById(section.id));
          }}
        >
          <span>{section.number.padStart(2, "0")}</span>{section.title}
        </a>
      </li>
    ))}
  </ul>
);

export const OfficialPolicyDocument = ({ policy, slug }) => {
  const [documentText, setDocumentText] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setDocumentText("");
    setError(false);
    fetch(policy.textPath, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Document unavailable");
        return response.text();
      })
      .then(setDocumentText)
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(true);
      });
    return () => controller.abort();
  }, [policy.textPath]);

  const sections = useMemo(() => documentText ? parseDocument(documentText) : [], [documentText]);

  return (
    <>
      <div className="policy-document-actions" data-testid={`policy-document-actions-${slug}`}>
        <div className="policy-document-label">
          <FileText size={19} aria-hidden="true" />
          <span><strong>Official policy</strong><small>{policy.pdfPath ? "PDF attachment" : "Web publication"} · Published {policy.lastUpdated}</small></span>
        </div>
        {policy.pdfPath && <div className="policy-action-links">
          <a href={policy.pdfPath} target="_blank" rel="noreferrer" data-testid={`policy-view-pdf-${slug}`}>
            <ExternalLink size={16} aria-hidden="true" />View PDF
          </a>
          <a href={policy.pdfPath} download data-testid={`policy-download-pdf-${slug}`}>
            <Download size={16} aria-hidden="true" />Download
          </a>
        </div>}
      </div>

      {error ? (
        <p className="policy-status" role="alert" data-testid={`policy-load-error-${slug}`}>The policy could not be loaded. Please try again later.</p>
      ) : !documentText ? (
        <div className="policy-status" aria-live="polite" data-testid={`policy-loading-${slug}`}>Loading official document…</div>
      ) : <>
        <div className="policy-layout">
          <details className="policy-mobile-toc" data-testid={`policy-mobile-toc-${slug}`}>
            <summary data-testid={`policy-mobile-toc-toggle-${slug}`}>Contents <span>{sections.length} sections</span></summary>
            <Toc sections={sections} mobile />
          </details>

          <aside className="policy-sidebar">
            <nav aria-label="Table of contents" className="policy-sidebar-inner" data-lenis-prevent>
              <p className="eyebrow mb-5">On this page</p>
              <Toc sections={sections} />
            </nav>
          </aside>

          <article className="policy-prose policy-official" data-testid={`policy-content-${slug}`}>
            {sections.map((section) => (
              <section key={section.id} data-testid={`policy-section-${section.number}`}>
                <h2 id={section.id}><span>{section.number.padStart(2, "0")}</span>{section.title}</h2>
                {section.body.map((item, index) => item.type === "list" ? (
                  <ul key={index}>{item.content.map((entry) => <li key={entry}>{entry}</li>)}</ul>
                ) : item.type === "subheading" ? (
                  <h3 key={index}>{item.content}</h3>
                ) : (
                  <p key={index}>{item.content}</p>
                ))}
              </section>
            ))}
          </article>
        </div>
        {slug === "master-policy" && <CrossPolicyMap />}
      </>}
    </>
  );
};